import { generatePath } from 'react-router-dom'
import queryString from 'query-string'
import { METABASE_SEARCH_MODELS } from '../api/metabase/client/search'

export const API_ENDPOINTS = {
  ACCOUNTS: '/accounts',
  ACCOUNT: (params: { accountId: string }) =>
    generatePath('/accounts/:accountId', params),
  ACCOUNT_SYNCS: (params: { accountId: string }) =>
    generatePath('/accounts/:accountId/syncs', params),
  ACCOUNT_SYNCS_CANCEL: (params: { accountId: string }) =>
    generatePath('/accounts/:accountId/syncs/cancel', params),
  ACCOUNT_LATEST_SYNC: (params: { accountId: string }) =>
    generatePath('/accounts/:accountId/syncs/latest', params),
  ACCOUNT_LOGS: (params: { accountId: string; syncStatusId: string }) =>
    generatePath('/accounts/:accountId/syncs/:syncStatusId/logs', params),
  ACCOUNT_CHANGELOG: (params: { accountId: string }) =>
    generatePath('/accounts/:accountId/changelog', params),
  ACCOUNTS_CHECK_CONNECTION: '/accounts/check_connection',
  API_KEYS: '/keys',
  API_KEY: (params: { keyId: string }) => generatePath('/keys/:keyId', params),
  GRAPHS: '/graphs',
  GRAPH_BY_NAME: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName', params),
  GRAPH_STATISTICS: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName/statistics', params),
  SECRET: (params: { secretName: string }) =>
    generatePath('/secrets/:secretName', params),
  SECRETS: '/secrets',
  USER_PROFILE: '/users/me',
  USER_NEW_ID: '/users/new-id',
  APPS: '/apps',
  APP_INVOCATION: (params: { appId: string }) =>
    generatePath('/apps/:appId/invocations', params),
  GRAPHS_GRAPHQL: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName/graphql', params),
  GRAPHS_MODELS: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName/models', params),
  GRAPHS_QUERIES: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName/queries', params),
  GRAPHS_REVISIONS: (params: { graphName: string }) =>
    generatePath('/graphs/:graphName/revisions', params),
  GRAPHS_REVISION_BY_ID: (params: { graphName: string; revisionId: string }) =>
    generatePath('/graphs/:graphName/revisions/:revisionId', params),
  REPORTS_SCHEDULES: '/reports/schedules',
  REPORT_SCHEDULE: (params: { scheduleId: string }) =>
    generatePath('/reports/schedules/:scheduleId', params),
  REPORT_SCHEDULE_RUNS: (params: { scheduleId: string }) =>
    generatePath('/reports/schedules/:scheduleId/runs', params),
  REPORTS_DASHBOARDS_VIEWS: '/reports/dashboards/views',
  REPORTS_DASHBOARDS_VIEW: (params: { viewId: string }) =>
    generatePath('/reports/dashboards/views/:viewId', params),
  REPORTS_DASHBOARDS_EMBEDDING: (params: {
    embeddingId: string
    pipelineId: string
  }) =>
    generatePath(
      '/reports/pipelines/:pipelineId/embeddings/:embeddingId',
      params
    ),
  REPORTS_PIPELINES: '/reports/pipelines',
  REPORTS_PIPELINE_STATE: (params: { pipelineId: string }) =>
    generatePath('/reports/pipelines/:pipelineId/state', params),
  REPORTS_PIPELINE_MODELS: (params: { pipelineId: string }) =>
    generatePath('/reports/pipelines/:pipelineId/models', params),
  REPORTS_PIPELINE_TRANSITION: (params: { pipelineId: string }) =>
    generatePath('/reports/pipelines/:pipelineId/transitions', params),
  REPORTS_DASHBOARDS_EMBEDDINGS: (params: { pipelineId: string }) =>
    generatePath('/reports/pipelines/:pipelineId/embeddings', params),
  REPORTS_PIPELINE_HIERARCHIES: (params: { pipelineId: string }) =>
    generatePath('/reports/pipelines/:pipelineId/hierarchies', params),
  NAMED_QUERIES: '/queries',
  NAMED_QUERY: (params: { name: string }) =>
    generatePath('/queries/:name', params),
  TEAM_DORA_METRICS: '/api/metabase/metrics/dora',
  DASHBOARD_PARAM_VALUES: (params: { dashboardId: string; paramId: string }) =>
    generatePath(
      '/api/metabase/dashboard/:dashboardId/params/:paramId/values',
      params
    ),
  DASHBOARD_PARAM_SEARCH: (params: {
    dashboardId: string
    paramId: string
    searchBy: string
  }) =>
    generatePath(
      '/api/metabase/dashboard/:dashboardId/params/:paramId/search/:searchBy',
      params
    ),
  AUTH_METABASE: ({
    basePath = '',
    ...params
  }: {
    basePath?: string
    graph: string
    jwt?: string
    return_to: string
  }) => `${basePath}/api/auth/metabase?${queryString.stringify(params)}`,
  AUTH_METABASE_SETUP: ({ basePath = '' }: { basePath?: string } = {}) =>
    `${basePath}/api/auth/metabase`,
  AUTH_README: ({
    basePath = '',
    ...params
  }: {
    basePath?: string
    jwt?: string
  }) => `${basePath}/api/auth/readme?${queryString.stringify(params)}`,
  AUTH_WORKFLOWS: '/api/auth/workflows',
  FLOWS_DEFINITIONS: '/reports/flows/definitions',
  FLOWS_DEFINITION: (params: { definitionId: string }) =>
    generatePath('/reports/flows/definitions/:definitionId', params),
  FLOWS_STEPS: '/reports/flows/steps',
  FLOWS_STEP: (params: { stepId: string }) =>
    generatePath('/reports/flows/steps/:stepId', params),
  FLOWS_ASSIGNMENTS: '/reports/flows/assignments',
  FLOWS_ASSIGNMENT: (params: { assignmentId: string }) =>
    generatePath('/reports/flows/assignments/:assignmentId', params),
  FLOWS_TAGS: '/reports/flows/tags',
  GEOCODING_LOOKUP: '/geocoding/lookup',
  SOURCES: '/api/sources',
  SOURCES_STATE: '/api/sources/state',
  SOURCES_SYNCS: '/api/sources/syncs',
  SOURCES_CANCEL: '/api/sources/cancel',
  SOURCES_CLONE: '/api/sources/clone',
  SOURCES_CUSTOM_DEFINITIONS: '/accounts/custom-definitions',
  SOURCES_CUSTOM_DEFINITION: (params: { type: string }) =>
    generatePath('/accounts/custom-definitions/:type', params),
  TEAMS: '/api/teams',
  TEAM: (params?: { id: string }) => generatePath('/api/teams/:id', params),
  SCORECARD_TEAM_BREAKDOWN: (params: { teamId: string }) =>
    generatePath('/api/teams/scorecard/:teamId', params),
  SCORECARD_METRIC: '/api/teams/scorecard-metric',
  PREFERENCES: '/preferences',
  PROXY: '/proxy',
  MODULES: '/api/metabase/module',
  MODULES_DEFAULT: '/api/metabase/module/default',
  AUTH: '/users/auth',
  PIPELINE_DATA_PERMISSIONS: '/api/pipelines/dataPermissions',
  ROLES: '/roles',
  CUSTOM_ROLE: (params: { role: string }) =>
    generatePath('/roles/:role', params),
  ROLE_SCOPES: (params: { role: string }) =>
    generatePath('/roles/:role/scopes', params),
  ROLE_SCOPES_RESET: (params: { role: string }) =>
    generatePath('/roles/:role/scopes/reset', params),
  PIPELINE_ROLES: (params: { graph: string; role: string }) =>
    generatePath('/api/pipelines/:graph/roles/:role', params),
  VALIDATE_CARD_BY_ID: (params: { cardId: string }) =>
    generatePath('/api/metabase/card/:cardId/validate', params),
  CARD: (params: { cardId: string }) =>
    generatePath('/api/metabase/card/:cardId', params),
  CARD_QUERY_FILTERED: (params: { cardId: string }) =>
    generatePath('/api/metabase/card/:cardId/query-filtered', params),
  CARD_METADATA: (params: { cardId: string }) =>
    generatePath('/api/metabase/card/:cardId/metadata', params),
  CARDS: '/api/metabase/card',
  ORG_UPLOAD_VALIDATE: '/api/org-upload/validate',
  ORG_UPLOAD_UPLOAD: '/api/org-upload/upload',
  NOTIFICATIONS: (params: { userId: string }) =>
    generatePath('/inboxes/:userId/notifications', params),
  AUTH_LINK: '/integrations/auth/link',
  PAGES: '/pages',
  UPDATE_PAGE: (params: { pageName: string }) =>
    generatePath('/pages/:pageName', params),
  RENAME_PAGE: (params: { pageName: string }) =>
    generatePath('/pages/:pageName/name', params),
  PAGES_IMAGES: '/pages/images',
  PAGES_IMAGE: (params: { imageId: string }) =>
    generatePath('/pages/images/:imageId', params),
  BLOG_RSS: '/api/blog/rss',
  SUMMARIZE_PAGE: '/api/lighthouse-ai/page-summarizer',
  SUMMARIZE_TOKEN_PAGE: '/api/lighthouse-ai/page-summary',
  DASHBOARD_QA: '/api/lighthouse-ai/dashboard-qa',
  EXPERIMENT_ANALYSIS: '/api/lighthouse-ai/experiment-analysis',
  README_FORM: '/api/auth/readme-form',
  SURVEYS_CREATE: '/api/surveys/create',
  SYSTEM_STATUS: '/api/status',
}

/**
 * @deprecated use named API_ENDPOINTS export instead
 */
export default { ...API_ENDPOINTS }

/**
 * https://www.metabase.com/docs/latest/api-documentation
 */
export const METABASE_API_PATHS = {
  SSO: '/auth/sso',
  DASHBOARDS: '/api/dashboard',
  DASHBOARD: (params: { id: number }) =>
    generatePath('/api/dashboard/:id', params),
  DASHBOARD_PARAM_VALUES: (params: { dashboardId: string; paramId: string }) =>
    generatePath('/api/dashboard/:dashboardId/params/:paramId/values', params),
  DASHBOARD_PARAM_SEARCH: (params: {
    dashboardId: string
    paramId: string
    searchBy: string
  }) =>
    generatePath(
      '/api/dashboard/:dashboardId/params/:paramId/search/:searchBy',
      params
    ),
  DATABASES: '/api/database',
  DATABASE: (params: { id: number }) =>
    generatePath('/api/database/:id', params),
  DATASET: '/api/dataset',
  CARD_QUERY: (params: {
    cardId: number
    dashboardId: number
    dashcardId: number
  }) =>
    generatePath(
      '/api/dashboard/:dashboardId/dashcard/:dashcardId/card/:cardId/query',
      params
    ),
  CARD_BY_ID: (params: { cardId: number }) => {
    return generatePath('/api/card/:cardId', params)
  },
  CARDS: '/api/card',
  CARD_BY_ID_QUERY: (params: { cardId: number }) =>
    generatePath('/api/card/:cardId/query', params),
  COLLECTIONS: {
    ROOT: '/api/collection',
    GRAPH: '/api/collection/graph',
    ROOT_ITEMS: '/api/collection/root/items',
    ITEMS: (params: { id: number }) =>
      generatePath('/api/collection/:id/items', params),
  },
  PERMISSIONS: {
    GROUP: '/api/permissions/group',
  },
  SEARCH: (params: { model: METABASE_SEARCH_MODELS }) =>
    `/api/search?models=${params.model}`,
} as const

// https://developer.atlassian.com/cloud/jira/platform/rest/v3/intro/
export const JIRA_CLOUD_API = {
  PAGINATED_PROJECTS: '/project/search',
  PERMISSIONS_CHECK: '/permissions/check',
} as const

// https://docs.atlassian.com/software/jira/docs/api/REST/9.7.0/
export const JIRA_SERVER_API = {
  PROJECTS: '/project',
  MY_PERMISSIONS: '/mypermissions',
} as const

export const GITHUB_API = {
  ROOT: 'https://api.github.com/',
  ORGANIZATIONS: 'https://api.github.com/user/orgs',
}
